import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/shallow";

import { useBoundStore } from "@/app/store";

interface UseFoldersHandlersProps {
  selectedFolders: Set<number>;
  setSelectedFolders: (folders: Set<number> | ((prev: Set<number>) => Set<number>)) => void;
  selectedNotes: Set<number>;
  isSelectionMode: boolean;
  setIsSelectionMode: (mode: boolean) => void;
  deleteFolder: (folderId: number) => Promise<void>;
  clearSelection: () => void;
}

export function useFoldersHandlers({
  selectedFolders,
  setSelectedFolders,
  selectedNotes,
  isSelectionMode,
  setIsSelectionMode,
  deleteFolder,
  clearSelection,
}: UseFoldersHandlersProps) {
  const navigate = useNavigate();
  const deviceType = useBoundStore(useShallow((state) => state.device.type));

  const handleDeleteSelected = useCallback(() => {
    // Delete selected folders
    selectedFolders.forEach((folderId) => {
      deleteFolder(folderId);
    });

    // Clear selection after deletion
    clearSelection();
  }, [selectedFolders, deleteFolder, clearSelection]);

  const handleFolderSelect = useCallback(
    (folderId: number) => {
      const wasSelected = selectedFolders.has(folderId);
      const newFoldersSize = wasSelected ? selectedFolders.size - 1 : selectedFolders.size + 1;

      setSelectedFolders((prev) => {
        const newSelected = new Set(prev);
        if (newSelected.has(folderId)) {
          newSelected.delete(folderId);
        } else {
          newSelected.add(folderId);
        }
        return newSelected;
      });

      // Exit selection mode if no folders are selected and no notes are selected
      if (newFoldersSize === 0 && selectedNotes.size === 0) {
        setIsSelectionMode(false);
      } else if (!isSelectionMode) {
        setIsSelectionMode(true);
      }
    },
    [selectedNotes.size, selectedFolders, setSelectedFolders, setIsSelectionMode, isSelectionMode]
  );

  const handleFolderLongPress = useCallback(
    (folderId: number) => {
      if (deviceType === "mobile") {
        handleFolderSelect(folderId);
      }
    },
    [deviceType, handleFolderSelect]
  );

  const handleFolderPress = useCallback(
    (id: number, _wasKeyboard?: boolean, ctrlKey?: boolean, longPress?: boolean) => {
      // Handle long press on mobile
      if (longPress) {
        handleFolderLongPress(id);
        return;
      }

      // Handle Ctrl+click on desktop for selection
      if (ctrlKey && deviceType === "desktop") {
        handleFolderSelect(id);
        return;
      }

      // Handle selection mode clicks
      if (isSelectionMode) {
        handleFolderSelect(id);
        return;
      }

      // Normal navigation
      navigate(`/notes/${id}`);
    },
    [navigate, isSelectionMode, handleFolderSelect, handleFolderLongPress, deviceType]
  );

  return {
    handleDeleteSelected,
    handleFolderSelect,
    handleFolderLongPress,
    handleFolderPress,
  };
}
